import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { unwrap } from '@/api/envelope';
import { useGetReportHistory } from '@/api/generated/moderation/moderation';
import type { HistoryResponseDTO } from '@/api/generated/model';
import { isPostHidden } from '../lib/contentDisplay';

/**
 * Content detail reports panel — real report history count via GET /admin/api/moderation/report/history.
 * "Current Restrictions" reflects post hide status (postStatus HID). Send-notification stays disabled
 * (no user system-notification endpoint — CMP parity).
 */
export function ContentReportsCard({
  contentId,
  postStatus,
}: {
  contentId: string;
  postStatus?: string | null;
}) {
  const id = Number(contentId);
  const valid = Number.isFinite(id) && id > 0;
  const query = useGetReportHistory(
    { requestDTO: { contentId: id, contentType: 'P' } },
    {
      query: {
        enabled: valid,
        select: (res) => unwrap<HistoryResponseDTO[] | undefined>(res) ?? [],
      },
    },
  );

  const reportCount = query.data?.length ?? 0;
  const restricted = isPostHidden(postStatus);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Reports</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {query.isLoading ? (
          <div className="flex justify-center py-2">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : query.isError ? (
          <p className="text-destructive">Failed to load report history.</p>
        ) : (
          <>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Reports</span>
              <span>{reportCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Restrictions</span>
              <span>{restricted ? 'YES' : 'NO'}</span>
            </div>
          </>
        )}
        <Button variant="outline" size="sm" className="w-full" disabled>
          Send System Notification
        </Button>
      </CardContent>
    </Card>
  );
}
